import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => n === null || n === undefined ? '' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });

/** Merge base_price + online_price events into a single time-indexed series. */
const buildChartData = (series) => {
  if (!series) return [];
  const points = new Map();
  const put = (date, key, value) => {
    if (!points.has(date)) points.set(date, { date });
    points.get(date)[key] = value;
  };
  for (const p of series.base_price || []) put(p.at.slice(0, 10), 'base_price', p.price);
  for (const p of series.online_price || []) put(p.at.slice(0, 10), 'online_price', p.price);
  return Array.from(points.values()).sort((a, b) => a.date.localeCompare(b.date));
};

export const PriceHistorySection = () => {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState('');
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/products`).then(r => {
      const list = Array.isArray(r.data) ? r.data : r.data.items || [];
      setProducts(list);
      if (list.length && !selected) setSelected(list[0].id);
    }).catch(() => toast.error('Failed to load products'));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selected) return;
    axios.get(`${API_URL}/analytics/price-history?product_id=${selected}`)
      .then(r => setPayload(r.data))
      .catch(() => toast.error('Failed to load price history'));
  }, [selected]);

  const series = useMemo(() => (payload?.series?.[0] || null), [payload]);
  const chartData = useMemo(() => buildChartData(series), [series]);

  return (
    <div className="bg-white rounded-xl border border-border shadow-sm p-5 space-y-4" data-testid="price-history-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-primary" />Price history</h2>
          <p className="text-xs text-muted-foreground">Every recorded base_price + online_price change per product.</p>
        </div>
        <div className="w-72">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger data-testid="price-history-product-select"><SelectValue placeholder="Pick a product…" /></SelectTrigger>
            <SelectContent>
              {products.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {chartData.length === 0 ? (
        <div className="text-center text-muted-foreground py-10 text-sm" data-testid="price-history-empty">No price changes recorded yet for this product. Update its base price or online price to start tracking.</div>
      ) : (
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${fmt(v)}`} />
              <Tooltip formatter={(v) => `RWF ${fmt(v)}`} />
              <Legend />
              <Line name="Base price" type="stepAfter" dataKey="base_price" stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} connectNulls />
              <Line name="Online price" type="stepAfter" dataKey="online_price" stroke="#0ea5e9" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} isAnimationActive={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default PriceHistorySection;
